import React, { useEffect, useState } from "react";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import moment from "moment";
import KpiStatCard from "../components/KpiStatCard";
import SiteSearchSelector from "../components/SiteSearchSelector";
import { safeText } from "../utils/hubHelpers";

export default function StockLedger({ onOpenCreateGrn }) {
  const [loading, setLoading] = useState(false);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [selectedType, setSelectedType] = useState(""); // '' | 'IN' | 'OUT' | 'TRANSFER'
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchSites();
    fetchLedgerData();
  }, [selectedSiteId]);

  const fetchSites = async () => {
    try {
      const res = await callApi(apiUrl.get_site, { method: "GET" });
      if (res?.status === true) {
        const list = Array.isArray(res?.data?.site)
          ? res.data.site
          : Array.isArray(res?.data)
          ? res.data
          : [];
        setSites(list);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchLedgerData = async () => {
    setLoading(true);
    try {
      let siteFilter = selectedSiteId ? `&siteId=${selectedSiteId}` : "";

      const [grnRes, stockOutRes, transferRes] = await Promise.all([
        callApi(`${apiUrl.get_GRN}?pageSize=100${siteFilter}`, { method: "GET" }),
        callApi(`${apiUrl.get_stock_out}?pageSize=100${siteFilter}`, { method: "GET" }),
        callApi(`${apiUrl.get_transfer_asset}?pageSize=100${siteFilter}`, { method: "GET" }),
      ]);

      const entries = [];

      // 1. Process GRN (Stock IN)
      if (grnRes?.status === true) {
        const grnList = Array.isArray(grnRes?.data?.grn)
          ? grnRes.data.grn
          : Array.isArray(grnRes?.data)
          ? grnRes.data
          : [];

        grnList.forEach((grn) => {
          const items = Array.isArray(grn.grnitems) ? grn.grnitems : [];
          if (items.length > 0) {
            items.forEach((it) => {
              entries.push({
                id: `GRN-${grn.id}-${it.id || Math.random()}`,
                rawDate: grn.date || grn.createdAt,
                date: moment(grn.date || grn.createdAt).format("DD MMM YYYY"),
                type: "IN",
                typeLabel: "Stock IN (GRN)",
                typeColor: "#059669",
                typeBg: "#dcfce7",
                refNo: grn.grnNumber || `GRN-${grn.id}`,
                siteName: grn.site?.name || "Site",
                siteId: grn.siteId || grn.site?.id,
                itemName: it.item?.name || it.name || "Goods Received",
                unit: it.item?.unit || it.unit || "Nos",
                quantity: parseFloat(it.quantity || it.receivedQuantity) || 1,
                direction: "+",
                partyOrDestination: grn.vendor?.firmName || grn.vendor?.name || "Supplier Delivery",
                remarks: grn.remarks || grn.poNumber || "PO Receipt",
                user: grn.user?.name || "SCM Store",
              });
            });
          } else {
            entries.push({
              id: `GRN-${grn.id}`,
              rawDate: grn.date || grn.createdAt,
              date: moment(grn.date || grn.createdAt).format("DD MMM YYYY"),
              type: "IN",
              typeLabel: "Stock IN (GRN)",
              typeColor: "#059669",
              typeBg: "#dcfce7",
              refNo: grn.grnNumber || `GRN-${grn.id}`,
              siteName: grn.site?.name || "Site",
              siteId: grn.siteId || grn.site?.id,
              itemName: "Batch Material Inward",
              unit: "Lot",
              quantity: 1,
              direction: "+",
              partyOrDestination: grn.vendor?.firmName || "Supplier Delivery",
              remarks: grn.remarks || "Goods Received Note",
              user: grn.user?.name || "Store",
            });
          }
        });
      }

      // 2. Process Stock Out (Stock OUT)
      if (stockOutRes?.status === true) {
        const soList = Array.isArray(stockOutRes?.data?.stockOut)
          ? stockOutRes.data.stockOut
          : Array.isArray(stockOutRes?.data)
          ? stockOutRes.data
          : [];

        soList.forEach((so) => {
          const items = Array.isArray(so.StockOutItems) ? so.StockOutItems : [];
          if (items.length > 0) {
            items.forEach((it) => {
              entries.push({
                id: `SO-${so.id}-${it.id || Math.random()}`,
                rawDate: so.date || so.createdAt,
                date: moment(so.date || so.createdAt).format("DD MMM YYYY"),
                type: "OUT",
                typeLabel: "Stock OUT (Issue)",
                typeColor: "#dc2626",
                typeBg: "#fee2e2",
                refNo: so.soNumber || `SO-${so.id}`,
                siteName: so.site?.name || "Site",
                siteId: so.siteId || so.site?.id,
                itemName: it.item?.name || it.name || "Issued Material",
                unit: it.item?.unit || it.unit || "Nos",
                quantity: parseFloat(it.quantity) || 1,
                direction: "-",
                partyOrDestination: so.type || "Site Consumption",
                remarks: it.remarks || so.description || "Issued to Equipment",
                user: so.user?.name || "Store Keeper",
              });
            });
          } else {
            entries.push({
              id: `SO-${so.id}`,
              rawDate: so.date || so.createdAt,
              date: moment(so.date || so.createdAt).format("DD MMM YYYY"),
              type: "OUT",
              typeLabel: "Stock OUT (Issue)",
              typeColor: "#dc2626",
              typeBg: "#fee2e2",
              refNo: so.soNumber || `SO-${so.id}`,
              siteName: so.site?.name || "Site",
              siteId: so.siteId || so.site?.id,
              itemName: "General Consumption / Dispatch",
              unit: "Units",
              quantity: 1,
              direction: "-",
              partyOrDestination: so.type || "Site Consumption",
              remarks: so.description || "Material Issue Voucher",
              user: so.user?.name || "Store Keeper",
            });
          }
        });
      }

      // 3. Process Transfers (TRANSFER)
      if (transferRes?.status === true) {
        const taList = Array.isArray(transferRes?.data?.transferAsset)
          ? transferRes.data.transferAsset
          : Array.isArray(transferRes?.data)
          ? transferRes.data
          : [];

        taList.forEach((ta) => {
          entries.push({
            id: `TA-${ta.id}`,
            rawDate: ta.date || ta.createdAt,
            date: moment(ta.date || ta.createdAt).format("DD MMM YYYY"),
            type: "TRANSFER",
            typeLabel: "Transfer Asset",
            typeColor: "#2563eb",
            typeBg: "#eff6ff",
            refNo: ta.taNumber || `TA-${ta.id}`,
            siteName: ta.fromSite?.name || "Origin Site",
            siteId: ta.fromSiteId || ta.fromSite?.id,
            itemName: ta.description || "Machinery / Asset Relocation",
            unit: "Unit",
            quantity: 1,
            direction: "⇄",
            partyOrDestination: `➔ ${ta.toSite?.name || "Destination Site"}`,
            remarks: ta.description || "Inter-site transfer",
            user: ta.user?.name || "Operations",
          });
        });
      }

      // Sort newest first
      entries.sort((a, b) => moment(b.rawDate).valueOf() - moment(a.rawDate).valueOf());
      setLedgerEntries(entries);
    } catch (e) {
      console.error(e);
      setLedgerEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const safeEntries = Array.isArray(ledgerEntries) ? ledgerEntries : [];

  const filteredEntries = safeEntries.filter((e) => {
    if (!e) return false;
    if (selectedType && e.type !== selectedType) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const item = (e.itemName || "").toLowerCase();
      const ref = (e.refNo || "").toLowerCase();
      const site = (e.siteName || "").toLowerCase();
      const party = (e.partyOrDestination || "").toLowerCase();
      const rem = (e.remarks || "").toLowerCase();
      if (!item.includes(q) && !ref.includes(q) && !site.includes(q) && !party.includes(q) && !rem.includes(q)) return false;
    }
    return true;
  });

  const totalTransactions = safeEntries.length;
  const inCount = safeEntries.filter((e) => e.type === "IN").length;
  const outCount = safeEntries.filter((e) => e.type === "OUT").length;
  const transferCount = safeEntries.filter((e) => e.type === "TRANSFER").length;

  return (
    <div>
      {/* 1. KPI Stats Grid */}
      <div className="kpi-grid">
        <KpiStatCard
          label="Total Ledger Entries"
          value={totalTransactions}
          subtext="Audited stock movements"
          icon={<span>📜</span>}
          color="#3b82f6"
          iconBg="#eff6ff"
        />

        <KpiStatCard
          label="Stock IN Transactions"
          value={inCount}
          subtext="GRN & supplier deliveries"
          icon={<span>📥</span>}
          color="#10b981"
          iconBg="#ecfdf5"
        />

        <KpiStatCard
          label="Stock OUT Dispatches"
          value={outCount}
          subtext="Issued to machines & sites"
          icon={<span>📤</span>}
          color="#ef4444"
          iconBg="#fef2f2"
        />

        <KpiStatCard
          label="Inter-Site Relocations"
          value={transferCount}
          subtext="Waybill asset transfers"
          icon={<span>⇄</span>}
          color="#8b5cf6"
          iconBg="#f5f3ff"
        />
      </div>

      {/* 2. Searchable Site Selector */}
      {(() => {
        const countsMap = sites.reduce((acc, site) => {
          const sId = site?.id || "";
          acc[sId] = safeEntries.filter((e) => e.siteId === sId).length;
          return acc;
        }, {});

        return (
          <SiteSearchSelector
            sites={sites}
            selectedSiteId={selectedSiteId}
            onSelectSite={setSelectedSiteId}
            countsMap={countsMap}
            totalCount={safeEntries.length}
          />
        );
      })()}

      {/* 3. Toolbar */}
      <div className="hub-toolbar">
        <div className="toolbar-left">
          <div className="search-box-wrap">
            <span className="search-icon-pos">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search reference #, item name, party or site..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <select
            className="filter-select"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <option value="">All Movement Types</option>
            <option value="IN">📥 Stock IN (GRN Receipt)</option>
            <option value="OUT">📤 Stock OUT (Material Issue)</option>
            <option value="TRANSFER">⇄ Inter-Site Transfer</option>
          </select>
        </div>

        <div className="toolbar-right" style={{ display: "flex", alignItems: "center", gap: "0.65rem" }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onOpenCreateGrn}
            style={{
              background: "#059669",
              borderColor: "#059669",
              borderRadius: 6,
              fontSize: "12.5px",
              height: 34,
              padding: "0 13px",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <span>📥</span> + Inward Goods (GRN)
          </button>

          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={fetchLedgerData}
            style={{
              borderRadius: 6,
              fontSize: "12.5px",
              height: 34,
              padding: "0 13px",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* 4. Ledger Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "4rem", color: "#64748b" }}>
          <div className="spinner-border text-primary" role="status" style={{ marginBottom: "1rem" }} />
          <p style={{ margin: 0, fontWeight: 600 }}>Assembling Chronological Stock Ledger...</p>
        </div>
      ) : filteredEntries.length === 0 ? (
        <div
          style={{
            background: "#ffffff",
            border: "1px dashed #cbd5e1",
            borderRadius: 12,
            padding: "4rem 2rem",
            textAlign: "center",
          }}
        >
          <span style={{ fontSize: "2.5rem", display: "block", marginBottom: "0.5rem" }}>📜</span>
          <h4 style={{ color: "#0f172a", fontWeight: 700 }}>No Stock Ledger Transactions</h4>
          <p style={{ color: "#64748b", maxWidth: 420, margin: "0 auto 1.5rem auto", fontSize: "0.875rem" }}>
            No stock in/out movements recorded for this filter. New GRNs and Stock-Out vouchers appear automatically in real-time.
          </p>
        </div>
      ) : (
        <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", color: "#64748b", textTransform: "uppercase", fontSize: "11.5px", letterSpacing: "0.04em" }}>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>Date & Time</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>Movement Type</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>Voucher Ref #</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>Site Location</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>Item / Asset Description</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "right", fontWeight: 700 }}>Quantity Movement</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>Party / Purpose / Receiver</th>
                <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontWeight: 700 }}>Logged By</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((e) => (
                <tr key={e.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "0.85rem 1rem", color: "#0f172a", fontWeight: 600, fontSize: "13px", textAlign: "left" }}>
                    {e.date}
                  </td>
                  <td style={{ padding: "0.85rem 1rem", textAlign: "left" }}>
                    <span
                      style={{
                        padding: "3px 8px",
                        borderRadius: 12,
                        fontSize: "12px",
                        fontWeight: 700,
                        color: e.typeColor,
                        background: e.typeBg,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      {e.typeLabel}
                    </span>
                  </td>
                  <td style={{ padding: "0.85rem 1rem", fontFamily: "monospace", fontWeight: 700, color: "#2563eb", fontSize: "13px", textAlign: "left" }}>
                    {safeText(e.refNo, "—")}
                  </td>
                  <td style={{ padding: "0.85rem 1rem", color: "#334155", fontWeight: 500, fontSize: "13px", textAlign: "left" }}>
                    📍 {safeText(e.siteName, "Site")}
                  </td>
                  <td style={{ padding: "0.85rem 1rem", textAlign: "left" }}>
                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "13.5px", textAlign: "left" }}>{safeText(e.itemName, "Item")}</div>
                    {e.remarks && <div style={{ fontSize: "12px", color: "#64748b", textAlign: "left" }}>{safeText(e.remarks)}</div>}
                  </td>
                  <td style={{ padding: "0.85rem 1rem", textAlign: "right" }}>
                    <span
                      style={{
                        fontWeight: 800,
                        fontSize: "13.5px",
                        color: e.direction === "+" ? "#059669" : e.direction === "-" ? "#dc2626" : "#2563eb",
                      }}
                    >
                      {e.direction}{e.quantity} {safeText(e.unit, "")}
                    </span>
                  </td>
                  <td style={{ padding: "0.85rem 1rem", color: "#475569", fontSize: "13px", textAlign: "left" }}>
                    {safeText(e.partyOrDestination, "—")}
                  </td>
                  <td style={{ padding: "0.85rem 1rem", color: "#64748b", fontSize: "13px", textAlign: "left" }}>
                    {safeText(e.user, "Store")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
